import { useEffect, useRef, useState } from 'react';
import { PERSONA_OPTIONS } from '../../constants/personas';

type PersonaDropdownProps = {
  selectedId: string;
  onSelect: (id: string) => void;
};

export function PersonaDropdown({ selectedId, onSelect }: PersonaDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeOption = PERSONA_OPTIONS.find((option) => option.id === selectedId);

  const label = activeOption?.title ?? 'Custom persona';
  const subtitle = activeOption?.subtitle ?? 'Using chat-specific configuration';

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-3 rounded-2xl border border-slate-700 bg-slate-900/70 px-4 py-2 text-left shadow-sm transition hover:border-slate-600"
      >
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Expert persona</p>
          <p className="text-sm font-semibold text-slate-100">{label}</p>
        </div>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+0.4rem)] z-50 w-64 rounded-2xl border border-slate-800 bg-slate-950/95 p-2 shadow-2xl">
          <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Personas
          </p>
          {PERSONA_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onSelect(option.id);
                setOpen(false);
              }}
              className={`w-full rounded-xl px-3 py-2 text-left transition ${
                option.id === selectedId
                  ? 'bg-slate-900 text-slate-100'
                  : 'text-slate-300 hover:bg-slate-900/70'
              }`}
            >
              <p className="text-sm font-medium">{option.title}</p>
              <p className="text-xs text-slate-500">{option.subtitle}</p>
            </button>
          ))}
        </div>
      )}

      <p className="mt-2 text-[11px] text-slate-500">{subtitle}</p>
    </div>
  );
}
